import { html } from '@effectivastudio/lit-components';
import BaseMenu from '@effectivastudio/lit-components/components/base_menu/element.js';
import translations from './translations.js';
import '@effectivastudio/lit-components/components/icon_block/element.js';
import '@effectivastudio/lit-components/components/dot_menu/element.js';

/**
 * The dot-dot-dot menu for booking slot
 *
 * @prop slotId - The id of a slot
 * @prop cardId - The id of a card
 * @prop approveOn - Show approve action
 * @prop declineOn - Show decline action
 * @prop cancelOn - Show cancel action
 * @prop undoOn - Show undo action
 * @prop contactOn - Show contact action
 * @prop editOn - Show edit action
 * @prop deleteOn - Show delete action
 * @prop contactUrl - Contact URL
 * @prop bookingSlot - Booking slot that owns this menu
 */

export default class BookingMenu extends BaseMenu {
  /** @internal */
  static translations = translations;

  static properties = {
    bookingSlot: { type: Object, attribute: false },
    slotId: { type: Number },
    cardId: { type: Number },
    approveOn: { type: Boolean },
    declineOn: { type: Boolean },
    cancelOn: { type: Boolean },
    undoOn: { type: Boolean },
    contactOn: { type: Boolean },
    contactUrl: { type: String },
    editOn: { type: Boolean },
    deleteOn: { type: Boolean },
  };

  /** @internal */
  get instanceId() {
    return `${this.cardId}_${this.slotId}`;
  }

  /** @internal */
  get baseUrl() {
    return `/booking_cards/${this.cardId}/slots/${this.slotId}`;
  }

  /** @internal */
  get approveItem() {
    return {
      title: this.t('actions.approve'),
      action: this.actionFor('slotPatch', [
        `${this.baseUrl}/approve`,
        'approve',
      ]),
      icon: 'check',
      cssClass: 'approve',
    };
  }

  /** @internal */
  get declineItem() {
    return {
      title: this.t('actions.decline'),
      action: this.actionFor('slotPatch', [
        `${this.baseUrl}/decline`,
        'decline',
      ]),
      icon: 'close',
      cssClass: 'decline',
    };
  }

  /** @internal */
  get undoItem() {
    return {
      title: this.t('actions.undo'),
      action: this.actionFor('slotPatch', [
        `${this.baseUrl}/undo_decline`,
        'undo_decline',
      ]),
      icon: 'check',
      cssClass: 'undo',
    };
  }

  /** @internal */
  get contactItem() {
    return {
      title: this.t('actions.contact'),
      action: this.actionFor('slotPost', [this.contactUrl]),
      icon: 'comment-alt-lines',
      cssClass: 'contact',
    };
  }

  /** @internal */
  get editItem() {
    return {
      title: this.t('actions.edit'),
      action: this.actionFor('slotGet', [`${this.baseUrl}/edit`]),
      icon: 'pencil',
      cssClass: 'edit',
    };
  }

  /** @internal */
  get cancelItem() {
    return {
      title: this.t('actions.cancel'),
      action: this.actionFor('slotCancel', [
        `${this.baseUrl}/cancel`,
        'cancel',
      ]),
      icon: 'close',
      cssClass: 'cancel',
    };
  }

  /** @internal */
  get deleteItem() {
    return {
      title: this.t('actions.delete'),
      action: this.actionFor('slotDelete'),
      role: 'destructive',
      icon: 'delete',
      cssClass: 'delete text-danger',
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get dividerOn() {
    return (
      (this.approveOn ||
        this.declineOn ||
        this.undoOn ||
        this.contactOn ||
        this.editOn) &&
      (this.cancelOn || this.deleteOn)
    );
  }

  /** @internal */
  get items() {
    return [
      this.approveOn && this.approveItem,
      this.declineOn && this.declineItem,
      this.undoOn && this.undoItem,
      this.contactOn && this.contactItem,
      this.editOn && this.editItem,
      this.dividerOn && this.divider,
      this.cancelOn && this.cancelItem,
      this.deleteOn && this.deleteItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    const { desc } = this.bookingSlot;

    return {
      buttons: this.items,
      ...(desc && { header: desc }),
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'bu-booking-menu',
      id: this.instanceId,
      method: method,
      ...(args && { args: args }),
    };
  }

  dispatchUpdatedEvent() {
    this.bookingSlot.dispatchEvent(
      new Event('bookingSlot-updated', { bubbles: true, composed: true })
    );
  }

  dispatchRemoveEvent() {
    this.bookingSlot.dispatchEvent(
      new Event('bookingSlot-remove', { bubbles: true, composed: true })
    );
  }

  async slotPatch(url, prop) {
    try {
      const res = await this.commonActions.patch(url, {
        headers: {
          Accept: 'application/json',
        },
      });

      const resJSON = JSON.parse(await res.text());

      Object.assign(this.bookingSlot, resJSON);
      this.dispatchUpdatedEvent();
      this.commonActions.displaySnackBarSuccess(
        this.t(`snack.${prop}_success`)
      );
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  async slotPost(url) {
    try {
      await this.commonActions.post(url);
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  async slotGet(url) {
    try {
      await this.commonActions.get(url);
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  async slotCancel(url, prop) {
    if (
      !this.commonActions.confirmDialog(this.t('confirm_dialog_msg.default'))
    ) {
      return false;
    }
    this.slotPatch(url, prop);
  }

  async slotDelete() {
    if (
      !this.commonActions.confirmDialog(this.t('confirm_dialog_msg.default'))
    ) {
      return false;
    }
    try {
      await this.commonActions.delete(this.baseUrl);
      this.dispatchRemoveEvent();
      this.bookingSlot.remove();
      this.commonActions.displaySnackBarSuccess(this.t('snack.delete_success'));
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  render() {
    return html`<ef-dot-menu .config=${this.dotMenuConfig}></ef-dot-menu>`;
  }
}

BookingMenu.registerTagName('bu-booking-menu');
BookingMenu.registerCommonActions('bu-booking-menu');

customElements.get('bu-booking-menu') ||
  customElements.define('bu-booking-menu', BookingMenu);
